import type { GetState, SetState } from "zustand";

import type { GoalCoachSnapshot, GoalImprovement, GoalImprovementState } from "../../types";
import type { AskPerformYardState } from "../types";

/**
 * Goal Quality Coach slice: the per-improvement state machine plus the bridge to the
 * goal wizard (snapshot in, `applyGoalImprovement` callback out).
 */
export const createGoalCoachSlice = (set: SetState<AskPerformYardState>, get: GetState<AskPerformYardState>) => ({
	goalImprovementStates: [] as GoalImprovementState[],
	currentGoalSnapshot: null as GoalCoachSnapshot | null,
	isOnGoalCreationPage: false,
	applyGoalImprovement: null as ((improvement: GoalImprovement) => void) | null,
	pendingGoalCoachStart: false,

	setCurrentGoalSnapshot: (snapshot: GoalCoachSnapshot | null) => set({ currentGoalSnapshot: snapshot }),

	setIsOnGoalCreationPage: (value: boolean) => set({ isOnGoalCreationPage: value }),

	setPendingGoalCoachStart: (value: boolean) => set({ pendingGoalCoachStart: value }),

	// Read-and-clear against the live store so the open effect fires once even under StrictMode's double-mount.
	consumePendingGoalCoachStart: (): boolean => {
		let wasPending = false;
		set((state) => {
			wasPending = state.pendingGoalCoachStart;
			return { pendingGoalCoachStart: false };
		});
		return wasPending;
	},

	setApplyGoalImprovement: (fn: ((improvement: GoalImprovement) => void) | null) => set({ applyGoalImprovement: fn }),

	setGoalImprovements: (messageId: string, suggestions: GoalImprovement[]) => {
		set((state) => {
			const msgIdx = state.messages.findIndex((m) => m.id === messageId);
			const updatedMessages = state.messages.slice();
			if (msgIdx !== -1) {
				updatedMessages[msgIdx] = { ...updatedMessages[msgIdx], goalImprovements: suggestions };
			}

			const newStates: GoalImprovementState[] = suggestions.map((s) => ({
				suggestion: s,
				status: "pending" as const,
				messageId,
			}));

			// Replace any existing states for this messageId; keep states from other messages.
			const otherStates = state.goalImprovementStates.filter((gs) => gs.messageId !== messageId);

			return {
				messages: updatedMessages,
				goalImprovementStates: [...otherStates, ...newStates],
			};
		});
	},

	markImprovementApplied: (suggestionId: string) => {
		set((state) => ({
			goalImprovementStates: state.goalImprovementStates.map((gs) =>
				gs.suggestion.id === suggestionId ? { ...gs, status: "applied" as const } : gs
			),
		}));
	},

	// Guard + mark + bridge in one action: only a pending suggestion applies, and only while
	// the goal wizard is mounted (no callback → don't mark a no-op as applied).
	applyImprovement: (suggestionId: string) => {
		const { goalImprovementStates, applyGoalImprovement } = get();
		const target = goalImprovementStates.find((gs) => gs.suggestion.id === suggestionId);
		if (!target || target.status !== "pending" || !applyGoalImprovement) return;
		applyGoalImprovement(target.suggestion);
		set((state) => ({
			goalImprovementStates: state.goalImprovementStates.map((gs) =>
				gs.suggestion.id === suggestionId ? { ...gs, status: "applied" as const } : gs
			),
		}));
	},

	markImprovementDeleted: (suggestionId: string) => {
		set((state) => ({
			goalImprovementStates: state.goalImprovementStates.map((gs) =>
				gs.suggestion.id === suggestionId ? { ...gs, status: "deleted" as const } : gs
			),
		}));
	},
});
